import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCategories } from '../services/api';
import { toast } from 'sonner';
import { Folder, ChevronRight, Tv2, Film, Newspaper, Music, Trophy, Heart, Globe } from 'lucide-react';

const categoryIcons = {
  'Sports': Trophy,
  'News': Newspaper,
  'Movies': Film,
  'Music': Music,
  'Entertainment': Tv2,
  'Kids': Heart,
  'International': Globe,
  'default': Folder
};

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        setCategories(data);
      } catch (error) {
        toast.error('Failed to load categories');
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (categoryName) => {
    navigate(`/?category=${encodeURIComponent(categoryName)}`);
  };

  const getIcon = (categoryName) => {
    const iconName = Object.keys(categoryIcons).find(key => 
      categoryName.toLowerCase().includes(key.toLowerCase())
    );
    return categoryIcons[iconName] || categoryIcons['default'];
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="categories-loading">
        <h1 className="text-3xl font-bold">Categories</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div>
        <h1 className="text-3xl font-bold">Categories</h1>
        <p className="text-white/60 mt-1">{categories.length} categories available</p>
      </div>

      {categories.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-[rgb(31,31,34)] flex items-center justify-center mb-6">
            <Folder className="w-10 h-10 text-white/30" />
          </div>
          <h3 className="text-xl font-semibold mb-2">No Categories</h3>
          <p className="text-white/50 max-w-md">Categories will appear once playlists are added</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => {
            const Icon = getIcon(category.name);
            return (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(category.name)}
                className="group flex items-center gap-4 p-5 rounded-xl bg-[rgb(15,15,16)] border border-white/5 hover:border-[rgb(124,58,237)]/50 transition-all text-left"
                data-testid={`category-${category.id}`}
              >
                <div className="w-14 h-14 rounded-xl bg-[rgb(124,58,237)]/20 flex items-center justify-center group-hover:bg-[rgb(124,58,237)]/30 transition-colors">
                  <Icon className="w-7 h-7 text-[rgb(124,58,237)]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">{category.name}</h3>
                  <p className="text-sm text-white/40">{category.channel_count} channels</p>
                </div>
                <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
